import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { toast } from '@shigo-ui/ui';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FilePicker,
  Form,
  Label,
  TagInput,
} from '@shigo-ui/ui';

import { useBusyDialogGuard } from '../../../../../hooks/useBusyDialogGuard';
import { dashboardT } from '../../../../../lib/messages';
import { assetUploadFormSchema, type AssetUploadFormValue } from '../../assetFormSchema';
import { useAssetMutations } from '../../hooks';
import { A2vUploadStatus } from '../../model';
import {
  assetFileId,
  assetTypeForFile,
  createAssetUploadQueueItem,
  hasValidAssetDimensions,
  isSupportedAssetFile,
  type AssetUploadQueueItem,
} from './AssetUploadDialog.helpers';
import { AssetUploadQueue } from './AssetUploadQueue';

interface AssetUploadDialogProps {
  onClose: () => void;
}

export function AssetUploadDialog({ onClose }: AssetUploadDialogProps) {
  'use no memo';
  const mutations = useAssetMutations();
  const [queue, setQueue] = React.useState<AssetUploadQueueItem[]>([]);
  const [uploading, setUploading] = React.useState(false);
  const guard = useBusyDialogGuard(uploading, onClose);
  const form = useForm<AssetUploadFormValue>({
    resolver: zodResolver(assetUploadFormSchema),
    defaultValues: { files: [], tags: [] },
  });
  const files = useWatch({ control: form.control, name: 'files' });
  const tags = useWatch({ control: form.control, name: 'tags' });
  const metadataLoading = queue.some((item) => item.status === A2vUploadStatus.LoadingMetadata);
  const uploadable = queue.filter(
    (item) => item.status === A2vUploadStatus.Pending || item.status === A2vUploadStatus.Error,
  );
  const readyUploadable = uploadable.filter(hasValidAssetDimensions);

  async function setFiles(nextFiles: File[]) {
    const supported = nextFiles.filter(isSupportedAssetFile);
    if (supported.length !== nextFiles.length)
      toast.error(dashboardT('a2v.assets.upload.unsupported'));
    const existing = new Map(queue.map((item) => [item.id, item]));
    const nextQueue = supported.map((file) => {
      const id = assetFileId(file);
      return (
        existing.get(id) ?? {
          id,
          file,
          assetType: assetTypeForFile(file),
          width: null,
          height: null,
          status: A2vUploadStatus.LoadingMetadata,
        }
      );
    }) satisfies AssetUploadQueueItem[];
    form.setValue('files', supported, { shouldDirty: true, shouldValidate: true });
    setQueue(nextQueue);

    await Promise.all(
      nextQueue.map(async (item) => {
        if (item.status !== A2vUploadStatus.LoadingMetadata) return;
        try {
          const resolved = await createAssetUploadQueueItem(item.file);
          setQueue((current) => current.map((row) => (row.id === item.id ? resolved : row)));
        } catch {
          setQueue((current) =>
            current.map((row) =>
              row.id === item.id
                ? {
                    ...row,
                    status: A2vUploadStatus.Error,
                    error: dashboardT('a2v.assets.upload.metadataError'),
                  }
                : row,
            ),
          );
        }
      }),
    );
  }

  async function submit() {
    if (!readyUploadable.length) return;
    setUploading(true);
    try {
      readyUploadable.forEach((item) => {
        updateQueueItem(item.id, { status: A2vUploadStatus.Uploading });
      });
      const results = await Promise.allSettled(
        readyUploadable.map((item) =>
          mutations.upload.mutateAsync({
            file: item.file,
            width: item.width,
            height: item.height,
            tags,
          }),
        ),
      );
      let successCount = 0;
      results.forEach((result, index) => {
        const item = readyUploadable[index];
        if (!item) return;
        if (result.status === 'fulfilled') {
          successCount += 1;
          updateQueueItem(item.id, { status: A2vUploadStatus.Success, error: undefined });
        } else {
          updateQueueItem(item.id, {
            status: A2vUploadStatus.Error,
            error: dashboardT('a2v.assets.upload.fileError'),
          });
        }
      });
      if (successCount) {
        toast.success(dashboardT('a2v.assets.upload.success', { count: successCount }));
      }
      // Failed or not-ready files keep the dialog open for retry.
      if (successCount === uploadable.length) onClose();
    } finally {
      setUploading(false);
    }
  }

  function updateQueueItem(id: string, update: Partial<AssetUploadQueueItem>) {
    setQueue((current) => current.map((item) => (item.id === id ? { ...item, ...update } : item)));
  }

  return (
    <Dialog open onOpenChange={guard.onOpenChange}>
      <DialogContent className="max-w-[700px] gap-0 overflow-hidden p-0">
        <DialogHeader className="border-border border-b px-5 py-3.5">
          <DialogTitle className="text-[15px] font-semibold">
            {dashboardT('a2v.assets.upload.title')}
          </DialogTitle>
          <DialogDescription>{dashboardT('a2v.assets.upload.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={(event) => void form.handleSubmit(submit)(event)} noValidate>
            <div className="max-h-[70vh] space-y-5 overflow-y-auto px-5 py-4">
              <Controller
                control={form.control}
                name="files"
                render={({ field, fieldState }) => (
                  <FilePicker
                    multiple
                    required
                    label={dashboardT('a2v.assets.upload.files')}
                    buttonLabel={dashboardT('a2v.assets.upload.chooseFiles')}
                    helperText={dashboardT('a2v.assets.upload.fileTypes')}
                    accept=".jpg,.jpeg,.svg,.png,.mp4"
                    value={field.value}
                    disabled={uploading}
                    error={fieldState.error?.message}
                    onFilesChange={(next) => void setFiles(next)}
                  />
                )}
              />
              <div className="space-y-1.5">
                <Label className="text-[13px] font-semibold">{dashboardT('a2v.assets.tags')}</Label>
                <Controller
                  control={form.control}
                  name="tags"
                  render={({ field }) => (
                    <TagInput
                      label={dashboardT('a2v.assets.tags')}
                      value={field.value}
                      onValueChange={field.onChange}
                      placeholder={dashboardT('a2v.assets.upload.tagsPlaceholder')}
                      disabled={uploading}
                    />
                  )}
                />
              </div>
              <AssetUploadQueue
                items={queue}
                disabled={uploading}
                onRemove={(id) => {
                  const nextFiles = files.filter((file) => assetFileId(file) !== id);
                  void setFiles(nextFiles);
                }}
              />
            </div>
            <DialogFooter className="border-border border-t px-5 py-3">
              <Button type="button" variant="outline" disabled={uploading} onClick={onClose}>
                {dashboardT('a2v.assets.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={uploading || metadataLoading || !readyUploadable.length}
              >
                {uploading
                  ? dashboardT('a2v.assets.upload.uploading')
                  : dashboardT('a2v.assets.upload.action')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
